import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import type { Collaborator, TargetProduct } from './types';
import { useCallback, useMemo, type MouseEvent } from 'react';
import {
	EnablementTouchpointEnum,
	type FetchInput,
	type FetchResponse,
	getTraceId,
	type Product,
	requestAccess as requestAccessService,
} from '@atlassian/expansion-services/services';
import {
	useServiceCore as useService,
	type DependencyOverridesOf,
} from '@atlassian/expansion-services/core';
import {
	OPERATIONAL_EVENT_TYPE,
	TRACK_EVENT_TYPE,
	UI_EVENT_TYPE,
	type GasPurePayload,
} from '@atlaskit/analytics-gas-types';

type Params = {
	tenantId: string;
	userId: string;
	targetProduct: TargetProduct;
	collaborators: Collaborator[];
	sourceProduct: Product;
	crossJoinUrl: string;
	showRequestSentFlag: () => void;
	messageTemplateId?: string;
	onComplete: () => void;
};

const targetProductByVariant: Record<TargetProduct, Product> = {
	loom: 'loom',
	'jira-product-discovery': 'jira-product-discovery',
	confluence: 'confluence',
};

const expansionServicesToPostOfficeMap = {
	sendTrackEvent: TRACK_EVENT_TYPE,
	sendOperationalEvent: OPERATIONAL_EVENT_TYPE,
	sendUIEvent: UI_EVENT_TYPE,
} as const;

type ExpansionServicesPayload = {
	type: keyof typeof expansionServicesToPostOfficeMap;
	data: GasPurePayload;
};

type RequestAccessDependencies = Omit<
	DependencyOverridesOf<typeof requestAccessService>,
	'createAnalyticsEvent'
> & { createAnalyticsEvent: ({ type, data }: ExpansionServicesPayload) => { fire: () => void } };

const createAnalyticsEventHandler = (
	fireAnalyticsEvent: ReturnType<typeof useFirePostOfficeAnalyticsEvents>['fireAnalyticsEvent'],
	messageTemplateId: string | undefined,
) => {
	return ({ type, data }: ExpansionServicesPayload) => ({
		fire: () => {
			const eventType = expansionServicesToPostOfficeMap[type];
			if (!eventType || !data.action) {
				// Unable to translate event type
				return;
			}

			fireAnalyticsEvent({
				eventType,
				action: data.action,
				actionSubject: data.actionSubject,
				actionSubjectId: data.actionSubjectId,
				attributes: { ...data.attributes, messageTemplateId },
			});
		},
	});
};

const fetchJSON = async <Response>(input: FetchInput): Promise<FetchResponse<Response>> => {
	const { url, ...options } = input;
	const response = await fetch(url, options);
	if (!response.ok) {
		throw new Error(`Fetch failed with status ${response.status}`);
	}
	const data = await response.json();
	const traceId = getTraceId(response);
	return traceId === undefined ? { data } : { data, traceId };
};

export const useRequestAccess = ({
	targetProduct,
	tenantId,
	userId,
	sourceProduct,
	collaborators,
	crossJoinUrl,
	showRequestSentFlag,
	messageTemplateId,
	onComplete,
}: Params): ((event: MouseEvent<HTMLAnchorElement>) => Promise<void>) => {
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const deps = useMemo(
		(): RequestAccessDependencies => ({
			createAnalyticsEvent: createAnalyticsEventHandler(fireAnalyticsEvent, messageTemplateId),
			fetchJSON,
			getStargateURL: (path: string) => `/gateway/api${path}`,
			getCurrentCloudId: () => tenantId,
			getCurrentProduct: () => sourceProduct,
			getCurrentUserId: () => userId,
		}),
		[fireAnalyticsEvent, tenantId, sourceProduct, userId],
	);
	const requestAccess = useService(
		requestAccessService,
		deps as DependencyOverridesOf<typeof requestAccessService>,
	);

	return useCallback(
		async (clickEvent: MouseEvent<HTMLAnchorElement>): Promise<void> => {
			clickEvent.preventDefault();
			void messageInstanceClicked({
				messageEventType: 'clicked',
				clickedElement: {
					clickIdentifier: 'cjnNav4',
					clickedElement: 'link',
				},
				action: {
					actionType: 'open',
					destinationUrl: crossJoinUrl,
				},
				additionalMetadataFromFrontend: {
					targetProduct,
					collaboratorCount: collaborators.length,
					...(messageTemplateId ? { messageTemplateId } : {}),
				},
			});
			void fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'cjnNav4',
				attributes: { messageTemplateId },
			});
			try {
				const result = await requestAccess({
					product: targetProductByVariant[targetProduct],
					integration: 'jira-side-nav-po-placement',
					touchpoint: EnablementTouchpointEnum.CROSSJOIN_NUDGE,
				});

				if (result.error) {
					fireAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'requestAccess',
						actionSubjectId: 'cjnNav4',
						attributes: {
							team: 'puma',
							targetProduct,
							messageTemplateId,
							errorMessage: result.error.message,
						},
					});
					if (typeof globalThis !== 'undefined' && globalThis.window) {
						globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
					}
				} else if (result.result === 'PENDING_REQUEST_CREATED') {
					showRequestSentFlag();
				} else if (typeof globalThis !== 'undefined' && globalThis.window) {
					globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
				}
			} catch (error) {
				fireAnalyticsEvent({
					eventType: 'operational',
					action: 'unexpectedFailure',
					actionSubject: 'requestAccess',
					actionSubjectId: 'cjnNav4',
					attributes: {
						team: 'puma',
						targetProduct,
						messageTemplateId,
						errorMessage: error instanceof Error ? error.message : undefined,
					},
				});
				if (typeof globalThis !== 'undefined' && globalThis.window) {
					globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
				}
			} finally {
				onComplete();
			}
		},
		[
			messageInstanceClicked,
			crossJoinUrl,
			targetProduct,
			collaborators,
			requestAccess,
			showRequestSentFlag,
			fireAnalyticsEvent,
		],
	);
};
